import type * as mapboxgl from 'mapbox-gl';

export interface ForestLayerDefinition {
  id: string;
  labelKey: string;
  tiles: string;
  maxzoom?: number;
  attribution: string;
}

const JRC_WMS =
  'SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&LAYERS=LAYER_NAME&STYLES=&CRS=EPSG:3857&BBOX={bbox-epsg-3857}&WIDTH=256&HEIGHT=256&FORMAT=image/png&TRANSPARENT=true';

export const FOREST_LAYERS: ForestLayerDefinition[] = [
  {
    id: 'jrc-gfc2020',
    labelKey: 'forest_layer_jrc_gfc2020_tr',
    tiles: `https://ies-ows.jrc.ec.europa.eu/iforce/gfc2020/wms.py?${JRC_WMS.replace('LAYER_NAME', 'gfc2020_v2')}`,
    attribution: 'JRC Global Forest Cover 2020',
  },
  {
    id: 'tmf-deforestation',
    labelKey: 'forest_layer_tmf_tr',
    tiles: `https://ies-ows.jrc.ec.europa.eu/iforce/tmf_v1/wms.py?${JRC_WMS.replace('LAYER_NAME', 'DeforestationYear')}`,
    attribution: 'JRC Tropical Moist Forest',
  },
  {
    id: 'hansen-loss',
    labelKey: 'forest_layer_hansen_tr',
    tiles: 'https://storage.googleapis.com/earthenginepartners-hansen/tiles/gfc_v1.11/loss_tree_gain/{z}/{x}/{y}.png',
    maxzoom: 12,
    attribution: 'Hansen/UMD/Google/USGS/NASA',
  },
];

export type Basemap = 'satellite' | 'streets';

export interface OverlayState {
  whisp: boolean;
  whispDebug: boolean;
  forest: Record<string, boolean>;
  basemap: Basemap;
  opacity: number;
}

export const DEFAULT_FOREST_OPACITY = 0.7;

export function defaultOverlayState(): OverlayState {
  return {
    whisp: true,
    whispDebug: false,
    forest: Object.fromEntries(FOREST_LAYERS.map(({ id }) => [id, true])),
    basemap: 'satellite',
    opacity: DEFAULT_FOREST_OPACITY,
  };
}

const STREETS_SOURCE = 'basemap-streets-src';
const STREETS_LAYER = 'basemap-streets';

const forestSourceId = (id: string) => `forest-${id}-src`;
const forestLayerId = (id: string) => `forest-${id}`;
const visibility = (visible: boolean) => (visible ? 'visible' : 'none');

export class ForestLayersController {
  constructor(
    private readonly map: mapboxgl.Map,
    private readonly accessToken: string
  ) {}

  attach(): void {
    this.addStreets();
    FOREST_LAYERS.forEach((definition) => this.addForest(definition));
  }

  apply(state: OverlayState): void {
    this.map.setLayoutProperty(STREETS_LAYER, 'visibility', visibility(state.basemap === 'streets'));
    for (const { id } of FOREST_LAYERS) {
      this.map.setLayoutProperty(forestLayerId(id), 'visibility', visibility(!!state.forest[id]));
      this.map.setPaintProperty(forestLayerId(id), 'raster-opacity', state.opacity);
    }
  }

  private addStreets(): void {
    this.map.addSource(STREETS_SOURCE, {
      type: 'raster',
      tiles: [
        `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/256/{z}/{x}/{y}?access_token=${this.accessToken}`,
      ],
      tileSize: 256,
    });
    this.map.addLayer({
      id: STREETS_LAYER,
      type: 'raster',
      source: STREETS_SOURCE,
      layout: { visibility: 'none' },
    });
  }

  private addForest({ id, tiles, maxzoom, attribution }: ForestLayerDefinition): void {
    this.map.addSource(forestSourceId(id), { type: 'raster', tiles: [tiles], tileSize: 256, maxzoom, attribution });
    this.map.addLayer({
      id: forestLayerId(id),
      type: 'raster',
      source: forestSourceId(id),
      layout: { visibility: 'none' },
      paint: { 'raster-opacity': DEFAULT_FOREST_OPACITY },
    });
  }
}
